import { Stack } from "@mantine/core";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EyeType, PupillometryTestResult, TestType } from "types/testTypes";
import PupillometryChart from "../../common/PupillometryChart";
import RAPDTables from "@components/testContainer/common/RAPDTables";
import PupillometryTables from "../../../new_test_ui/new_test_steps/test-configuration/extra-tests/pupillometry/PupillometryTables";
import {
  getEyeDataForPupillometry,
  getReferenceLinesForPupillometryDataset,
} from "@components/new_test_ui/new_test_steps/test-configuration/extra-tests/pupillometry/PupillometryCalculations";
import ClinicalSummaryCard from "../common/ClinicalSummaryCard";
import { ClinicalSummaryMetric } from "../utils/clinicalSummaryTypes";
import { interpretPupillometrySummary } from "../utils/pupillometryInterpretation";

const formatMetricValue = (metric: ClinicalSummaryMetric) => {
  if (metric.value === null || metric.value === undefined || metric.value === "") {
    return "—";
  }

  if (typeof metric.value === "string") {
    return metric.value;
  }

  const formattedValue =
    typeof metric.decimals === "number" ? metric.value.toFixed(metric.decimals) : `${metric.value}`;
  return `${formattedValue}${metric.unit ?? ""}`;
};

export default function PupillometryTestEntry({ entry }) {
  const { t } = useTranslation();
  const testResults = entry as PupillometryTestResult | null;
  const pupilTestType = testResults?.testType ?? null;

  const referenceLines = useMemo(
    () =>
      testResults?.results?.length
        ? getReferenceLinesForPupillometryDataset(testResults.results)
        : null,
    [testResults?.results],
  );

  const leftResults = useMemo(
    () =>
      pupilTestType === TestType.Pupillometry
        ? getEyeDataForPupillometry(testResults?.results ?? [], EyeType.LeftEye, "NORMAL")
        : null,
    [pupilTestType, testResults?.results],
  );
  const rightResults = useMemo(
    () =>
      pupilTestType === TestType.Pupillometry
        ? getEyeDataForPupillometry(testResults?.results ?? [], EyeType.RightEye, "NORMAL")
        : null,
    [pupilTestType, testResults?.results],
  );
  const leftResultsDirect = useMemo(
    () =>
      pupilTestType === TestType.RAPD
        ? getEyeDataForPupillometry(testResults?.results ?? [], EyeType.LeftEye, "DIRECT")
        : null,
    [pupilTestType, testResults?.results],
  );
  const rightResultsDirect = useMemo(
    () =>
      pupilTestType === TestType.RAPD
        ? getEyeDataForPupillometry(testResults?.results ?? [], EyeType.RightEye, "DIRECT")
        : null,
    [pupilTestType, testResults?.results],
  );
  const leftResultsConsensual = useMemo(
    () =>
      pupilTestType === TestType.RAPD
        ? getEyeDataForPupillometry(testResults?.results ?? [], EyeType.LeftEye, "CONSENTUAL")
        : null,
    [pupilTestType, testResults?.results],
  );
  const rightResultsConsensual = useMemo(
    () =>
      pupilTestType === TestType.RAPD
        ? getEyeDataForPupillometry(testResults?.results ?? [], EyeType.RightEye, "CONSENTUAL")
        : null,
    [pupilTestType, testResults?.results],
  );

  const interpretation = useMemo(
    () =>
      interpretPupillometrySummary({
        testType: pupilTestType,
        leftResults,
        rightResults,
        leftResultsDirect,
        rightResultsDirect,
        leftResultsConsensual,
        rightResultsConsensual,
      }),
    [
      leftResults,
      leftResultsConsensual,
      leftResultsDirect,
      pupilTestType,
      rightResults,
      rightResultsConsensual,
      rightResultsDirect,
    ],
  );

  return (
    <Stack gap="md" mb="md">
      {interpretation && (
        <ClinicalSummaryCard
          title={t(interpretation.titleKey)}
          explainer={t(interpretation.explainerKey)}
          riskLevel={interpretation.riskLevel}
          riskLabel={t(`extraTests.pupillometry.summary.risk.${interpretation.riskLevel}`)}
          reason={t(interpretation.reasonKey)}
          keyFindingsLabel={t("extraTests.pupillometry.summary.keyFindingsLabel")}
          keyFindings={interpretation.keyFindings}
          interpretationLabel={t("extraTests.pupillometry.summary.howToInterpretLabel")}
          interpretationNotes={interpretation.interpretationNotes}
          metrics={interpretation.metrics.map((metric) => ({
            label: t(metric.labelKey),
            value: formatMetricValue(metric),
          }))}
        />
      )}

      {testResults?.results && referenceLines && (
        <PupillometryChart
          data={testResults.results}
          referenceLines={referenceLines}
          height={260}
        />
      )}

      {leftResults && rightResults && (
        <PupillometryTables leftResults={leftResults} rightResults={rightResults} />
      )}

      {leftResultsDirect &&
        rightResultsDirect &&
        leftResultsConsensual &&
        rightResultsConsensual && (
          <RAPDTables
            leftResultsDirect={leftResultsDirect}
            leftResultsConsensual={leftResultsConsensual}
            rightResultsDirect={rightResultsDirect}
            rightResultsConsensual={rightResultsConsensual}
          />
        )}
    </Stack>
  );
}
